import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import ramaService from "../services/rama";
import { formatDate, parseDateWithoutTimezone } from "../utils/formatters";

const STATUS_ENTREGA = {
  0: "Pendente",
  1: "Aguardando análise",
  2: "Aprovada",
  3: "Reprovada",
  4: "Cancelada por prazo",
};

const STATUS_VERSAO = {
  0: "Aguardando análise",
  1: "Aprovada",
  2: "Reprovada",
};

function formatarDataLimiteRama(dataLimite) {
  if (!dataLimite) return "—";
  const raw = String(dataLimite);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const apenasData = raw.slice(0, 10);
    const date = parseDateWithoutTimezone(apenasData);
    if (!date) return "—";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }
  return formatDate(raw);
}

const RamaPanel = ({
  solicitacaoId,
  papel,
  licencaEmitida = false,
  onChanged,
}) => {
  const [plano, setPlano] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [entregaExpandida, setEntregaExpandida] = useState(null);
  const [arquivo, setArquivo] = useState(null);
  const [entregaEnvioId, setEntregaEnvioId] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [reprovarOpen, setReprovarOpen] = useState(false);
  const [versaoReprovarId, setVersaoReprovarId] = useState(null);
  const [motivoReprovacao, setMotivoReprovacao] = useState("");

  const carregar = useCallback(async () => {
    if (!solicitacaoId || !licencaEmitida) {
      setPlano(null);
      return;
    }
    setLoading(true);
    setErro("");
    try {
      const data = await ramaService.obterPorSolicitacao(solicitacaoId);
      setPlano(data && data.entregas ? data : null);
    } catch (e) {
      setErro(e.message || "Erro ao carregar plano de RAMA.");
      setPlano(null);
    } finally {
      setLoading(false);
    }
  }, [solicitacaoId, licencaEmitida]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (!licencaEmitida) return null;

  const podeEnviar = papel === "interessado";
  const podeAnalisar = papel === "analista";

  const handleEnviar = async () => {
    if (!arquivo || !entregaEnvioId) return;
    setEnviando(true);
    setErro("");
    try {
      await ramaService.enviar(entregaEnvioId, arquivo);
      setArquivo(null);
      setEntregaEnvioId(null);
      await carregar();
      onChanged?.();
    } catch (e) {
      setErro(e.message || e.detail || "Erro ao enviar RAMA.");
    } finally {
      setEnviando(false);
    }
  };

  const handleAprovar = async (versaoId) => {
    try {
      await ramaService.aprovar(versaoId);
      await carregar();
      onChanged?.();
    } catch (e) {
      setErro(e.message || "Erro ao aprovar RAMA.");
    }
  };

  const handleReprovar = async () => {
    if (!motivoReprovacao.trim()) {
      setErro("Informe o motivo da reprovação.");
      return;
    }
    try {
      await ramaService.reprovar(versaoReprovarId, motivoReprovacao.trim());
      setReprovarOpen(false);
      setMotivoReprovacao("");
      setVersaoReprovarId(null);
      await carregar();
      onChanged?.();
    } catch (e) {
      setErro(e.message || "Erro ao reprovar RAMA.");
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Relatório ambiental (RAMA)
      </Typography>

      {loading && <CircularProgress size={24} />}
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro("")}>
          {erro}
        </Alert>
      )}

      {!loading && !plano && (
        <Typography variant="body2" color="text.secondary">
          Esta licença não possui condicionante periódica de RAMA.
        </Typography>
      )}

      {plano && (
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Intervalo: {plano.intervaloMeses} mês(es) · {plano.quantidade}{" "}
            entrega(s)
          </Typography>
          <List dense>
            {(plano.entregas || []).map((entrega) => {
              const permiteEnvio =
                podeEnviar &&
                (entrega.status === 0 || entrega.status === 3);
              const versoes = [...(entrega.versoes || [])].sort(
                (a, b) => a.numeroVersao - b.numeroVersao,
              );
              const versaoPendente = versoes.find((v) => v.status === 0);

              return (
                <Box key={entrega.id} sx={{ mb: 1 }}>
                  <ListItem
                    secondaryAction={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          label={STATUS_ENTREGA[entrega.status] || entrega.status}
                        />
                        <Button
                          size="small"
                          onClick={() =>
                            setEntregaExpandida(
                              entregaExpandida === entrega.id
                                ? null
                                : entrega.id,
                            )
                          }
                        >
                          Histórico
                        </Button>
                      </Stack>
                    }
                  >
                    <ListItemText
                      primary={`Entrega ${entrega.numero}`}
                      secondary={`Prazo: ${formatarDataLimiteRama(entrega.dataLimite)}`}
                    />
                  </ListItem>

                  {permiteEnvio && (
                    <Stack direction="row" spacing={1} sx={{ px: 2, mb: 1 }}>
                      <Button
                        component="label"
                        size="small"
                        variant="outlined"
                      >
                        Selecionar arquivo
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.png,application/pdf,image/png"
                          onChange={(e) => {
                            setArquivo(e.target.files?.[0] || null);
                            setEntregaEnvioId(entrega.id);
                          }}
                        />
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={
                          !arquivo ||
                          entregaEnvioId !== entrega.id ||
                          enviando
                        }
                        onClick={handleEnviar}
                      >
                        Enviar RAMA
                      </Button>
                    </Stack>
                  )}

                  {podeAnalisar && versaoPendente && (
                    <Stack direction="row" spacing={1} sx={{ px: 2, mb: 1 }}>
                      <Button
                        size="small"
                        color="success"
                        variant="contained"
                        onClick={() => handleAprovar(versaoPendente.id)}
                      >
                        Aprovar v{versaoPendente.numeroVersao}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => {
                          setVersaoReprovarId(versaoPendente.id);
                          setReprovarOpen(true);
                        }}
                      >
                        Reprovar
                      </Button>
                    </Stack>
                  )}

                  <Collapse in={entregaExpandida === entrega.id}>
                    <Box sx={{ pl: 3, pr: 2, pb: 1 }}>
                      {versoes.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Nenhuma versão enviada.
                        </Typography>
                      )}
                      {versoes.map((versao) => (
                        <Box key={versao.id} sx={{ mb: 1 }}>
                          <Typography variant="body2">
                            v{versao.numeroVersao} —{" "}
                            {STATUS_VERSAO[versao.status]} —{" "}
                            {versao.fileName}
                          </Typography>
                          {versao.motivoReprovacao && (
                            <Typography variant="caption" color="error">
                              Motivo: {versao.motivoReprovacao}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </List>
        </Stack>
      )}

      <Dialog open={reprovarOpen} onClose={() => setReprovarOpen(false)}>
        <DialogTitle>Reprovar RAMA</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Motivo da reprovação"
            value={motivoReprovacao}
            onChange={(e) => setMotivoReprovacao(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReprovarOpen(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleReprovar}>
            Reprovar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const RamaAnaliseFields = ({
  solicitacaoId,
  dataValidade,
  setDataValidade,
  possuiRama,
  setPossuiRama,
  intervaloRamaMeses,
  setIntervaloRamaMeses,
  disabled,
}) => {
  const [preview, setPreview] = useState(null);
  const [previewErro, setPreviewErro] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!possuiRama || !dataValidade || !intervaloRamaMeses || intervaloRamaMeses < 1) {
        setPreview(null);
        setPreviewErro("");
        return;
      }
      setLoadingPreview(true);
      setPreviewErro("");
      try {
        const licencasService = (await import("../services/licencas")).default;
        const data = await licencasService.previewRama(
          solicitacaoId,
          dataValidade,
          Number(intervaloRamaMeses),
        );
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) {
          setPreview(null);
          setPreviewErro(
            e.message ||
              e.title ||
              "Não foi possível calcular as entregas previstas.",
          );
        }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    };
    const timer = setTimeout(run, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [solicitacaoId, dataValidade, possuiRama, intervaloRamaMeses]);

  return (
    <Box sx={{ mb: 2 }}>
      <TextField
        fullWidth
        type="date"
        label="Data de validade da licença"
        InputLabelProps={{ shrink: true }}
        value={dataValidade || ""}
        onChange={(e) => setDataValidade(e.target.value)}
        helperText="Data até a qual a licença permanece válida (fim do prazo ambiental)."
        sx={{ mb: 2, mt: 0.5 }}
        disabled={disabled}
      />

      <FormControlLabel
        control={
          <Switch
            checked={!!possuiRama}
            onChange={(e) => setPossuiRama(e.target.checked)}
            disabled={disabled}
          />
        }
        label="Exigir entregas periódicas de RAMA"
        sx={{ mb: 1 }}
      />

      {possuiRama && (
        <TextField
          fullWidth
          type="number"
          label="Intervalo entre entregas (meses)"
          value={intervaloRamaMeses ?? ""}
          onChange={(e) => setIntervaloRamaMeses(e.target.value)}
          inputProps={{ min: 1 }}
          sx={{ mb: 2 }}
          disabled={disabled}
        />
      )}

      {possuiRama && loadingPreview && <CircularProgress size={20} />}
      {possuiRama && previewErro && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {previewErro}
        </Alert>
      )}
      {possuiRama && preview && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {preview.quantidade} entrega(s) prevista(s)
          {preview.datasProvisorias
            ? " (datas provisórias; firmadas na emissão)."
            : "."}
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            {preview.entregas?.map((item) => (
              <li key={item.numero}>
                Entrega {item.numero}: {formatarDataLimiteRama(item.dataLimite)}
              </li>
            ))}
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default RamaPanel;
